import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	addLeadContact,
	createLead,
	deleteLead,
	deleteLeadContact,
	deleteLeadEvent,
	getLeadById,
	getLeads,
	getOutreachAccess,
	importLeadsCsv,
	logLeadEvent,
	sendLeadEmail,
	updateLead,
	updateLeadContact,
	verifyLeadContactEmails,
} from '../../services/CRM/outreachService';

export const LEADS_KEY = 'outreach-leads';

export const useOutreachAccess = (companyId) =>
	useQuery({
		queryKey: [LEADS_KEY, 'access', companyId],
		queryFn: getOutreachAccess,
		enabled: !!companyId,
		staleTime: 60 * 1000,
	});

export const useLeadsList = (companyId) =>
	useQuery({
		queryKey: [LEADS_KEY, companyId],
		queryFn: () => getLeads(companyId),
		enabled: !!companyId,
	});

export const useLead = (companyId, leadId) =>
	useQuery({
		queryKey: [LEADS_KEY, companyId, leadId],
		queryFn: () => getLeadById(leadId, companyId),
		enabled: !!companyId && !!leadId,
	});

/** Puts a lead returned by the API into the detail cache and the list cache. */
const useStoreLead = (companyId) => {
	const queryClient = useQueryClient();
	return (lead) => {
		queryClient.setQueryData([LEADS_KEY, companyId, lead._id], lead);
		queryClient.setQueryData([LEADS_KEY, companyId], (list) =>
			Array.isArray(list)
				? list.map((l) => (l._id === lead._id ? lead : l))
				: list,
		);
	};
};

/** Mutation for any endpoint that returns the updated lead. */
const useLeadMutation = (companyId, mutationFn) => {
	const store = useStoreLead(companyId);
	return useMutation({ mutationFn, onSuccess: store });
};

export const useUpdateLead = (companyId) =>
	useLeadMutation(companyId, ({ id, data }) => updateLead(id, data, companyId));

export const useLogLeadEvent = (companyId) =>
	useLeadMutation(companyId, ({ id, data }) =>
		logLeadEvent(id, data, companyId),
	);

export const useSendLeadEmail = (companyId) =>
	useLeadMutation(companyId, ({ id, kind }) =>
		sendLeadEmail(id, kind, companyId),
	);

export const useDeleteLeadEvent = (companyId) =>
	useLeadMutation(companyId, ({ id, eventId }) =>
		deleteLeadEvent(id, eventId, companyId),
	);

export const useSaveLeadContact = (companyId) =>
	useLeadMutation(companyId, ({ id, contactId, data }) =>
		contactId
			? updateLeadContact(id, contactId, data, companyId)
			: addLeadContact(id, data, companyId),
	);

export const useDeleteLeadContact = (companyId) =>
	useLeadMutation(companyId, ({ id, contactId }) =>
		deleteLeadContact(id, contactId, companyId),
	);

/** Also refreshes the credit balance and verification history the check just changed. */
export const useVerifyLeadContacts = (companyId) => {
	const queryClient = useQueryClient();
	const store = useStoreLead(companyId);
	return useMutation({
		mutationFn: ({ id, contactIds, provider }) =>
			verifyLeadContactEmails(id, { contactIds, provider }, companyId),
		onSuccess: store,
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: ['verificationCredits'] });
			queryClient.invalidateQueries({ queryKey: ['emailVerificationJobs'] });
		},
	});
};

const useListMutation = (mutationFn) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn,
		onSuccess: () => queryClient.invalidateQueries({ queryKey: [LEADS_KEY] }),
	});
};

export const useCreateLead = (companyId) =>
	useListMutation((data) => createLead(data, companyId));

export const useImportLeads = (companyId) =>
	useListMutation((csv) => importLeadsCsv(csv, companyId));

export const useDeleteLead = (companyId) =>
	useListMutation((id) => deleteLead(id, companyId));
